import React, { useCallback, useEffect, useState } from "react";
import useApi from "@/hooks/useApi";

// ─── Types ──────────────────────────────────────────────────────────────────

interface SourceStat {
  count: number;
  chunks: number;
  lastIndexedAt: string | null;
}

interface IndexQuota {
  chunksUsed: number;
  chunksLimit: number;
  jobsThisMonth: number;
  jobsLimit: number;
  pagesThisMonth: number;
  pagesLimit: number;
  autoRebuildEnabled: boolean;
  rebuildIntervalDays: number;
}

interface SavedUrlSet {
  id: number;
  name: string;
  urlCount: number;
  lastRunAt: string | null;
  lastPagesChanged: number;
  autoRebuild: boolean;
}

interface IndexStats {
  sources: {
    kb_articles?: SourceStat;
    shopify_products?: SourceStat;
    bigcommerce_products?: SourceStat;
    scraper_pages?: SourceStat;
    youtube_content?: SourceStat;
    pricing_catalog?: SourceStat;
    custom_content?: SourceStat;
  };
  quota: IndexQuota;
  savedUrlSets: SavedUrlSet[];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(iso: string | null): string {
  if (!iso) return "Never";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  } catch {
    return "—";
  }
}

function QuotaBar({
  label,
  used,
  limit,
  color
}: {
  label: string;
  used: number;
  limit: number;
  color: string;
}) {
  if (limit === 0) {
    return (
      <div className="mb-3">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-slate-600">{label}</span>
          <span className="text-slate-400">Unlimited</span>
        </div>
        <div className="h-2 bg-slate-100 rounded-full" />
      </div>
    );
  }

  const pct = Math.min(100, Math.round((used / limit) * 100));
  const barColor =
    pct >= 90
      ? "bg-red-500"
      : pct >= 70
      ? "bg-amber-400"
      : color;

  return (
    <div className="mb-3">
      <div className="flex justify-between text-sm mb-1">
        <span className="text-slate-600">{label}</span>
        <span className="text-slate-500">
          {used.toLocaleString()} / {limit.toLocaleString()} ({pct}%)
        </span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div
          className={`h-2 rounded-full transition-all duration-300 ${barColor}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function KnowledgeIndexPanel() {
  const api = useApi();
  const [stats, setStats] = useState<IndexStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [stale, setStale] = useState(false);
  const [runningSetId, setRunningSetId] = useState<number | null>(null);
  const [runError, setRunError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setStale(false);
    try {
      const resp = await api.messaging.get("scraper/kb/index-stats");
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = (await resp.json()) as IndexStats;
      setStats(data);
    } catch {
      setStale(true);
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleRunNow = async (setId: number) => {
    setRunningSetId(setId);
    setRunError(null);
    try {
      const resp = await api.messaging.post(`scraper/url-sets/${setId}/run`, {});
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      // Refresh stats after a short delay so the UI reflects the new job
      setTimeout(fetchStats, 1500);
    } catch {
      setRunError(`Failed to start job for set ${setId}.`);
    } finally {
      setRunningSetId(null);
    }
  };

  // ── Source rows definition ────────────────────────────────────────────────

  const sourceRows: {
    key: keyof IndexStats["sources"];
    icon: string;
    label: string;
  }[] = [
    { key: "kb_articles", icon: "📚", label: "KB Articles" },
    { key: "shopify_products", icon: "🛍️", label: "Shopify Products" },
    { key: "bigcommerce_products", icon: "🏪", label: "BigCommerce Products" },
    { key: "scraper_pages", icon: "🌐", label: "Web Pages" },
    { key: "youtube_content", icon: "📹", label: "YouTube Videos" },
    { key: "pricing_catalog", icon: "💰", label: "Pricing Catalog" },
    { key: "custom_content", icon: "📄", label: "Custom Content" }
  ];

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-400 text-sm">
        <svg
          className="animate-spin h-5 w-5 mr-2 text-cornflower-blue-500"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v8H4z"
          />
        </svg>
        Loading index stats…
      </div>
    );
  }

  const sources = stats?.sources ?? {};
  const quota = stats?.quota;
  const urlSets = Array.isArray(stats?.savedUrlSets) ? stats!.savedUrlSets : [];
  const totalChunks = sourceRows.reduce(
    (sum, r) => sum + (sources[r.key]?.chunks ?? 0),
    0
  );
  const totalDocs = sourceRows.reduce(
    (sum, r) => sum + (sources[r.key]?.count ?? 0),
    0
  );

  return (
    <div className="mt-4">
      {/* Header row */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <span className="font-semibold text-base">Knowledge Index</span>
          <span className="ml-3 text-slate-400 text-sm">
            {totalDocs.toLocaleString()} documents · {totalChunks.toLocaleString()} chunks
          </span>
          {stale && (
            <span className="ml-3 text-amber-600 text-sm font-medium">
              ⚠ Could not refresh — showing cached data
            </span>
          )}
        </div>
        <button
          className="btn-blue btn-sm py-1.5 px-3 text-sm"
          onClick={fetchStats}
          disabled={loading}
        >
          ↺ Refresh
        </button>
      </div>

      {/* Sources table */}
      <div className="border rounded-xl overflow-hidden mb-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <th className="text-left px-4 py-2 font-medium">Source</th>
              <th className="text-right px-4 py-2 font-medium">Documents</th>
              <th className="text-right px-4 py-2 font-medium">Chunks</th>
              <th className="text-right px-4 py-2 font-medium">Last Updated</th>
            </tr>
          </thead>
          <tbody>
            {sourceRows.map((row, i) => {
              const s = sources[row.key];
              const hasData = (s?.count ?? 0) > 0;
              return (
                <tr
                  key={row.key}
                  className={`border-t ${i % 2 === 0 ? "" : "bg-slate-50/40"}`}
                >
                  <td className="px-4 py-2.5 flex items-center gap-2">
                    <span>{row.icon}</span>
                    <span className={hasData ? "text-slate-700" : "text-slate-400"}>
                      {row.label}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {hasData ? (
                      <span className="font-medium text-slate-700">
                        {(s?.count ?? 0).toLocaleString()}
                      </span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {hasData ? (
                      <span className="font-medium text-slate-700">
                        {(s?.chunks ?? 0).toLocaleString()}
                      </span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right text-slate-500">
                    {formatDate(s?.lastIndexedAt ?? null)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Quota section */}
      {quota && (
        <div className="border rounded-xl p-5 mb-6">
          <div className="font-semibold text-sm mb-4 text-slate-700">
            Quota Usage
            {quota.autoRebuildEnabled && (
              <span className="ml-2 text-xs font-normal text-success-green-600 bg-success-green-50 px-2 py-0.5 rounded-full border border-success-green-200">
                Auto-rebuild every {quota.rebuildIntervalDays}d
              </span>
            )}
          </div>
          <QuotaBar
            label="Index Chunks"
            used={quota.chunksUsed ?? 0}
            limit={quota.chunksLimit ?? 0}
            color="bg-cornflower-blue-500"
          />
          <QuotaBar
            label="Crawl Jobs This Month"
            used={quota.jobsThisMonth ?? 0}
            limit={quota.jobsLimit ?? 0}
            color="bg-amber-400"
          />
          <QuotaBar
            label="Pages Crawled This Month"
            used={quota.pagesThisMonth ?? 0}
            limit={quota.pagesLimit ?? 0}
            color="bg-success-green-500"
          />
        </div>
      )}

      {/* Saved URL sets */}
      <div className="border rounded-xl overflow-hidden mb-2">
        <div className="px-4 py-3 bg-slate-50 border-b">
          <span className="font-semibold text-sm text-slate-700">
            Saved URL Sets
          </span>
          <span className="ml-2 text-slate-400 text-xs">
            ({urlSets.length} configured)
          </span>
        </div>
        {urlSets.length === 0 ? (
          <div className="px-4 py-6 text-center text-slate-400 text-sm">
            No URL sets configured. Add one in the Website tab to start crawling.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-slate-500 text-xs uppercase tracking-wide border-b">
                <th className="text-left px-4 py-2 font-medium">Name</th>
                <th className="text-right px-4 py-2 font-medium">URLs</th>
                <th className="text-right px-4 py-2 font-medium">Last Run</th>
                <th className="text-right px-4 py-2 font-medium">Pages Changed</th>
                <th className="text-right px-4 py-2 font-medium">Auto-Rebuild</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {urlSets.map((us) => (
                <tr key={us.id} className="border-t hover:bg-slate-50/50">
                  <td className="px-4 py-2.5 font-medium text-slate-700">
                    {us.name}
                  </td>
                  <td className="px-4 py-2.5 text-right text-slate-500">
                    {us.urlCount}
                  </td>
                  <td className="px-4 py-2.5 text-right text-slate-500">
                    {formatDate(us.lastRunAt)}
                  </td>
                  <td className="px-4 py-2.5 text-right text-slate-500">
                    {us.lastPagesChanged > 0 ? (
                      <span className="text-cornflower-blue-600 font-medium">
                        +{us.lastPagesChanged}
                      </span>
                    ) : (
                      "0"
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {us.autoRebuild ? (
                      <span className="text-xs bg-success-green-50 text-success-green-700 border border-success-green-200 rounded-full px-2 py-0.5">
                        On
                      </span>
                    ) : (
                      <span className="text-xs bg-slate-100 text-slate-400 rounded-full px-2 py-0.5">
                        Off
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button
                      className="btn-blue btn-sm py-1 px-3 text-xs disabled:opacity-50"
                      onClick={() => handleRunNow(us.id)}
                      disabled={runningSetId === us.id}
                    >
                      {runningSetId === us.id ? "Starting…" : "Run Now"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {runError && (
        <div className="text-red-600 text-sm mt-2">{runError}</div>
      )}
    </div>
  );
}
